import { useEffect, useState } from "react";

import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';

import { domain, token } from "../../../../Security";
import axios from "../../../../../api/apiClient";
import { DetailRow } from "../formComponents/DetailRow";
import { base64ToFile } from "../../../../../utils/fileUtils";
import { TextArea } from "../../../../../common/Input";
import { WorkflowStatus } from "../../../../../utils/enums";
import { Button } from "../../../../../common/APICommon";
import { ConfirmationModal, PDFModal } from "../../../../../common/Modal";

export const CandidateDetailsModal = ({ visible, onClose, mrfStatus, candidate, showGeneralMessage, setLoading, onCandidateUpdate }) => {
  const [requesterComment, setRequesterComment] = useState("");
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({});

  useEffect(() => { if (visible && candidate) setRequesterComment(candidate.requesterComments || ""); }, [visible, candidate]);
  if (!visible) return null;

  const handleAction = async (isAccept) => {
    setLoading(true);
    try {
      const apiData = {
        isAccepted: isAccept, isDeclined: !isAccept,
        requesterComments: requesterComment || (isAccept ? "Accepted" : "Declined"),
        acceptedDate: isAccept ? new Date().toISOString() : null,
        declinedDate: !isAccept ? new Date().toISOString() : null,
      };
      const res = await axios.put(domain + `/api/Candidate/update-status/${candidate.id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      onCandidateUpdate(res.data);
      showGeneralMessage(`Candidate ${candidate.fullName} has been ${isAccept ? 'accepted' : 'declined'}.`, true);
      onClose();
    } catch (error) {
      showGeneralMessage("Failed to update candidate status.", false);
    } finally {
      setShowConfirmModal(false); setLoading(false);
    }
  };

  return (
    <div role="dialog" className="fixed inset-0 z-[10001] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto">
      <div className="bg-gray-50 w-full max-w-[80%] rounded-lg shadow-xl h-[90vh] flex flex-col">
        <div className="flex-shrink-0 flex justify-between items-center px-6 py-2 border-b bg-white rounded-t-lg">
          <h2 className="text-xl font-semibold flex items-center space-x-2"><PersonIcon fontSize="large" className="text-blue-400" /><span>Candidate Details</span></h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1 bg-transparent border-none"><CloseIcon /></button>
        </div>
        <div className="flex-grow overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-300">
            <h3 className="text-sm font-bold text-gray-500 uppercase border-b pb-1 mb-3">Basic Information</h3>
            <DetailRow label="Full Name" value={candidate.fullName} />
            <DetailRow label="Email" value={candidate.email} />
            <DetailRow label="Contact" value={candidate.contactNumber} />
            <DetailRow label="Skills" value={candidate.skills} />
            <DetailRow label="Experience" value={candidate.experience} />
            {candidate.file && <button onClick={() => { const file = base64ToFile(candidate.file, candidate.fileName, 'application/pdf'); setCurrentPdfUrl(URL.createObjectURL(file)); setPdfModalVisible(true); }} className="mt-4 bg-blue-500 text-white w-full py-2 rounded-md border-none cursor-pointer hover:bg-blue-600 transition-colors">View Resume</button>}
          </div>
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white p-4 rounded-lg border border-gray-300 shadow-sm">
              <h3 className="text-sm font-bold text-gray-500 uppercase border-b pb-1 mb-2">Qualifications & Remarks</h3>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{candidate.candidateQualificationNotes || "No notes provided"}</p>
              <h3 className="text-sm font-bold text-gray-500 uppercase border-b pb-1 mt-4 mb-2">HR Remarks</h3>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{candidate.processorComments || "No HR remarks provided"}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-300 shadow-sm">
              <TextArea label="Requester Notes (Optional)" value={requesterComment} onChange={(e) => setRequesterComment(e.target.value)} isDisabled={mrfStatus !== WorkflowStatus.PROCESSING} rows={4} />
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-2 p-6 border-t border-gray-200 sticky bottom-0 bg-white z-10 rounded-b-lg shadow-sm">
          <Button
            name="Decline"
            className="bg-red-500 hover:bg-red-600 text-white"
            onClick={
              () => {
                setConfirmConfig({
                  title: "Decline Candidate", message: "Are you sure you want to proceed with declining this candidate?",
                  onConfirm: () => handleAction(false), type: "warning"
                });
                setShowConfirmModal(true);
              }
            }
            disabled={mrfStatus !== WorkflowStatus.PROCESSING}
          />
          <Button
            name="Accept"
            className="bg-green-500 hover:bg-green-600 text-white"
            onClick={
              () => {
                setConfirmConfig({
                  title: "Accept Candidate", message: "Are you sure you want to accept this candidate?",
                  onConfirm: () => handleAction(true), type: "info"
                });
                setShowConfirmModal(true);
              }}
            disabled={mrfStatus !== WorkflowStatus.PROCESSING}
          />
        </div>
      </div>
      <PDFModal
        visible={pdfModalVisible}
        fileUrl={currentPdfUrl}
        onClose={
          () => {
            setPdfModalVisible(false);
            URL.revokeObjectURL(currentPdfUrl);
          }}
      />
      <ConfirmationModal
        visible={showConfirmModal}
        onClose={
          () =>
            setShowConfirmModal(false)
        }
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText="Confirm"
        type={confirmConfig.type}
      />
    </div>
  );
};